import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { isMasterAccount } from "@/lib/auth";
import { Loader2 } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  // Use ref to prevent double execution (React StrictMode or fast re-mounts)
  const hasProcessed = useRef(false);
  // Capture redirect target immediately on first render to prevent race conditions
  const redirectTarget = useRef<string | null>(null);

  // Capture the redirect target immediately (before any async operations)
  if (redirectTarget.current === null) {
    redirectTarget.current = localStorage.getItem("auth_redirect_to") || "";
    // Clear immediately to prevent any other reads getting stale data
    localStorage.removeItem("auth_redirect_to");
  }

  useEffect(() => {
    // Prevent double execution
    if (hasProcessed.current) {
      return;
    }
    hasProcessed.current = true;

    const handleCallback = async () => {
      try {
        if (!supabase) {
          throw new Error("Supabase not configured");
        }

        // Check for error in URL params first
        const errorParam = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");
        if (errorParam || errorDescription) {
          throw new Error(errorDescription || errorParam || "Authentication failed");
        }

        // For OAuth PKCE flow, we need to exchange the code for a session
        // The code comes as a query parameter from the OAuth provider
        const code = searchParams.get("code");

        let session = null;

        if (code) {
          // Exchange the OAuth code for a session
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            if (import.meta.env.DEV) console.error("Code exchange error:", exchangeError);
            throw exchangeError;
          }

          session = data.session;
        } else {
          // Try to get existing session (for magic link or hash fragment auth)
          const { data: { session: existingSession }, error: sessionError } = await supabase.auth.getSession();

          if (sessionError) {
            throw sessionError;
          }

          session = existingSession;
        }

        if (!session) {
          throw new Error("Authentication failed - no session established");
        }

        // Use the captured redirect target (captured on first render)
        const storedRedirect = redirectTarget.current;

        if (storedRedirect) {
          // User selected a specific portal, go there
          if (import.meta.env.DEV) console.log("Auth callback: Redirecting to stored target:", storedRedirect);
          navigate(storedRedirect, { replace: true });
        } else {
          // No specific portal selected, use defaults
          const userEmail = session.user.email;
          if (import.meta.env.DEV) console.log("Auth callback: No stored redirect, using default for:", userEmail);

          if (isMasterAccount(userEmail)) {
            // Master accounts go to sales dashboard by default
            navigate("/sales", { replace: true });
          } else {
            // Regular users go to business dashboard
            navigate("/app/dashboard", { replace: true });
          }
        }
      } catch (err) {
        if (import.meta.env.DEV) console.error("Auth callback error:", err);
        setError(err instanceof Error ? err.message : "Authentication failed");
      }
    };

    handleCallback();
  }, [navigate, searchParams]);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Authentication Error</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <a
            href="/login"
            className="inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Try Again
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center" role="status" aria-live="polite">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}
